/**
 * Insurance Lead Queue Page
 * Main page for insurance agents to manage customer leads
 */

import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { canAccessInsurance, isInsuranceOnly } from '../auth/permissions';
import { InsuranceLeadQueue, InsuranceReports } from '../components/insurance';

type TabType = 'queue' | 'reports';

export const Insurance: React.FC = () => {
  const { user, isAdmin, isExecutive } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('queue');

  // Check permissions
  const canViewReports = isAdmin || isExecutive || isInsuranceOnly(user?.role || null);
  const insuranceOnly = isInsuranceOnly(user?.role || null);

  // Permission check
  if (!canAccessInsurance(user?.role || null)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="mt-2 text-gray-600">
            You don't have permission to access the Insurance Lead Queue.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Insurance Lead Queue</h1>
          <p className="mt-1 text-sm text-gray-600">
            {insuranceOnly
              ? 'Manage and process insurance leads from VinSolutions.'
              : 'View and manage insurance leads across all locations.'}
          </p>
        </div>

        {/* Tab Navigation (show for users who can view reports) */}
        {canViewReports && (
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('queue')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'queue'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Lead Queue
              </button>
              <button
                onClick={() => setActiveTab('reports')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'reports'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Reports
              </button>
            </nav>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'queue' && (
          <InsuranceLeadQueue isAdmin={isAdmin || isExecutive} />
        )}

        {activeTab === 'reports' && canViewReports && (
          <InsuranceReports isAdmin={isAdmin || isExecutive} />
        )}
      </div>
    </div>
  );
};

export default Insurance;
